import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  Checkbox,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useStores } from '../../store/store_context';
import { getRoleById } from '../../store/api_funcs';

// Валидационная схема для Formik и Yup
const validationSchema = Yup.object().shape({
  role_name: Yup.string().required('Обязательное поле'),
  see_all_panels: Yup.boolean().required('Обязательное поле'),
  add_panels: Yup.boolean().required('Обязательное поле'),
  update_firmware: Yup.boolean().required('Обязательное поле'),
  see_all_users: Yup.boolean().required('Обязательное поле'),
  add_users: Yup.boolean().required('Обязательное поле'),
  edit_themes: Yup.boolean().required('Обязательное поле'),
  get_errors: Yup.boolean().required('Обязательное поле'),
  add_roles: Yup.boolean().required('Обязательное поле'),
  only_myself: Yup.boolean().required('Обязательное поле'),
  only_my_panel: Yup.boolean().required('Обязательное поле'),
  analytics: Yup.boolean().required('Обязательное поле'),
  monitoring_view: Yup.boolean().required('Обязательное поле'),
  monitoring_control: Yup.boolean().required('Обязательное поле'),
  repair_console_access: Yup.boolean().required('Обязательное поле'),
  incidents_manage: Yup.boolean().required('Обязательное поле'),
  tickets_manage: Yup.boolean().required('Обязательное поле'),
  monitoring_settings: Yup.boolean().required('Обязательное поле'),
  remote_control_panel: Yup.boolean().required('Обязательное поле'),
});

const RoleEditForm = ({ onSubmit, updateRolesTable, role_id }) => {
  const [initialValues, setInitialValues] = useState(null);
  const { pageStore } = useStores();
  const toast = useToast();

  const getRoleInfo = async () => {
    const roleData = await getRoleById(role_id, pageStore.token);
    setInitialValues({
      role_name: roleData.role_name || '',
      see_all_panels: !!roleData.see_all_panels,
      add_panels: !!roleData.add_panels,
      update_firmware: !!roleData.update_firmware,
      see_all_users: !!roleData.see_all_users,
      add_users: !!roleData.add_users,
      edit_themes: !!roleData.edit_themes,
      get_errors: !!roleData.get_errors,
      add_roles: !!roleData.add_roles,
      only_myself: !!roleData.only_myself,
      only_my_panel: !!roleData.only_my_panel,
      analytics: !!roleData.analytics,
      monitoring_view: !!roleData.monitoring_view,
      monitoring_control: !!roleData.monitoring_control,
      repair_console_access: !!roleData.repair_console_access,
      incidents_manage: !!roleData.incidents_manage,
      tickets_manage: !!roleData.tickets_manage,
      monitoring_settings: !!roleData.monitoring_settings,
      remote_control_panel: !!roleData.remote_control_panel,
    });
  };

  useEffect(() => {
    getRoleInfo();
  }, [role_id]);

  const handleSubmit = async (values, actions) => {
    const transformedValues = {
      ...values,
      see_all_panels: values.see_all_panels ? 1 : 0,
      add_panels: values.add_panels ? 1 : 0,
      update_firmware: values.update_firmware ? 1 : 0,
      see_all_users: values.see_all_users ? 1 : 0,
      add_users: values.add_users ? 1 : 0,
      edit_themes: values.edit_themes ? 1 : 0,
      get_errors: values.get_errors ? 1 : 0,
      add_roles: values.add_roles ? 1 : 0,
      only_myself: values.only_myself ? 1 : 0,
      only_my_panel: values.only_my_panel ? 1 : 0,
      analytics: values.analytics ? 1 : 0,
      monitoring_view: values.monitoring_view ? 1 : 0,
      monitoring_control: values.monitoring_control ? 1 : 0,
      repair_console_access: values.repair_console_access ? 1 : 0,
      incidents_manage: values.incidents_manage ? 1 : 0,
      tickets_manage: values.tickets_manage ? 1 : 0,
      monitoring_settings: values.monitoring_settings ? 1 : 0,
      remote_control_panel: values.remote_control_panel ? 1 : 0,
    };
    try {
      await onSubmit(transformedValues, role_id, pageStore.token);
      toast({
        title: 'Роль успешно обновлена.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Ошибка при отправке данных:', error);
      toast({
        title: 'Ошибка',
        description: 'Что-то пошло не так. Пожалуйста, попробуйте снова.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      await updateRolesTable();
      actions.setSubmitting(false);
    }
  };

  const roleFormText = {
    RU: [
      'роль',
      'просмотр всех панелей',
      'добавление панелей',
      'обновление прошивки',
      'просмотр всех пользователей',
      'добавление пользователей',
      'редактирование тем',
      'получение отчетов',
      'добавление ролей',
      'только инфо о себе',
      'только инфо о своей панели',
      'просмотр аналитики'
    ],
    EN: [
      'role name',
      'see all panels',
      'add panels',
      'update firmware',
      'see all users',
      'add users',
      'edit themes',
      'get errors',
      'add roles',
      'only myself',
      'only my panel',
      'can see analytics'
    ],
  };

  if (!initialValues) {
    return <div>Loading...</div>; // Или можете вернуть индикатор загрузки
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched }) => (
        <Form>
          <Field name="role_name">
            {({ field }) => (
              <FormControl isInvalid={errors.role_name && touched.role_name}>
                <FormLabel htmlFor="role_name">
                  {roleFormText[pageStore.lang][0]}:
                </FormLabel>
                <Input {...field} id="role_name" />
              </FormControl>
            )}
          </Field>

          <Field name="see_all_panels">
            {({ field }) => (
              <FormControl
                isInvalid={errors.see_all_panels && touched.see_all_panels}
              >
                <FormLabel htmlFor="see_all_panels">
                  {roleFormText[pageStore.lang][1]}:
                </FormLabel>
                <Checkbox {...field} id="see_all_panels" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="add_panels">
            {({ field }) => (
              <FormControl isInvalid={errors.add_panels && touched.add_panels}>
                <FormLabel htmlFor="add_panels">
                  {roleFormText[pageStore.lang][2]}:
                </FormLabel>
                <Checkbox {...field} id="add_panels" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="update_firmware">
            {({ field }) => (
              <FormControl
                isInvalid={errors.update_firmware && touched.update_firmware}
              >
                <FormLabel htmlFor="update_firmware">
                  {roleFormText[pageStore.lang][3]}:
                </FormLabel>
                <Checkbox {...field} id="update_firmware" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="see_all_users">
            {({ field }) => (
              <FormControl
                isInvalid={errors.see_all_users && touched.see_all_users}
              >
                <FormLabel htmlFor="see_all_users">
                  {roleFormText[pageStore.lang][4]}:
                </FormLabel>
                <Checkbox {...field} id="see_all_users" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="add_users">
            {({ field }) => (
              <FormControl isInvalid={errors.add_users && touched.add_users}>
                <FormLabel htmlFor="add_users">
                  {roleFormText[pageStore.lang][5]}:
                </FormLabel>
                <Checkbox {...field} id="add_users" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="edit_themes">
            {({ field }) => (
              <FormControl
                isInvalid={errors.edit_themes && touched.edit_themes}
              >
                <FormLabel htmlFor="edit_themes">
                  {roleFormText[pageStore.lang][6]}:
                </FormLabel>
                <Checkbox {...field} id="edit_themes" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="get_errors">
            {({ field }) => (
              <FormControl isInvalid={errors.get_errors && touched.get_errors}>
                <FormLabel htmlFor="get_errors">
                  {roleFormText[pageStore.lang][7]}:
                </FormLabel>
                <Checkbox {...field} id="get_errors" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="add_roles">
            {({ field }) => (
              <FormControl isInvalid={errors.add_roles && touched.add_roles}>
                <FormLabel htmlFor="add_roles">
                  {roleFormText[pageStore.lang][8]}:
                </FormLabel>
                <Checkbox {...field} id="add_roles" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="only_myself">
            {({ field }) => (
              <FormControl
                isInvalid={errors.only_myself && touched.only_myself}
              >
                <FormLabel htmlFor="only_myself">
                  {roleFormText[pageStore.lang][9]}:
                </FormLabel>
                <Checkbox {...field} id="only_myself" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="only_my_panel">
            {({ field }) => (
              <FormControl
                isInvalid={errors.only_my_panel && touched.only_my_panel}
              >
                <FormLabel htmlFor="only_my_panel">
                  {roleFormText[pageStore.lang][10]}:
                </FormLabel>
                <Checkbox {...field} id="only_my_panel" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="analytics">
            {({ field }) => (
              <FormControl
                isInvalid={errors.analytics && touched.analytics}
              >
                <FormLabel htmlFor="analytics">
                  {roleFormText[pageStore.lang][11]}:
                </FormLabel>
                <Checkbox {...field} id="analytics" isChecked={field.value} />
              </FormControl>
            )}
          </Field>
          <Field name="monitoring_view">
            {({ field }) => (
              <FormControl isInvalid={errors.monitoring_view && touched.monitoring_view}>
                <FormLabel htmlFor="monitoring_view">просмотр мониторинга:</FormLabel>
                <Checkbox {...field} id="monitoring_view" isChecked={field.value} />
              </FormControl>
            )}
          </Field>

          <Field name="monitoring_control">
            {({ field }) => (
              <FormControl isInvalid={errors.monitoring_control && touched.monitoring_control}>
                <FormLabel htmlFor="monitoring_control">управление мониторингом:</FormLabel>
                <Checkbox {...field} id="monitoring_control" isChecked={field.value} />
              </FormControl>
            )}
          </Field>

          <Field name="repair_console_access">
            {({ field }) => (
              <FormControl isInvalid={errors.repair_console_access && touched.repair_console_access}>
                <FormLabel htmlFor="repair_console_access">доступ к ремонтной консоли:</FormLabel>
                <Checkbox {...field} id="repair_console_access" isChecked={field.value} />
              </FormControl>
            )}
          </Field>

          <Field name="incidents_manage">
            {({ field }) => (
              <FormControl isInvalid={errors.incidents_manage && touched.incidents_manage}>
                <FormLabel htmlFor="incidents_manage">управление инцидентами:</FormLabel>
                <Checkbox {...field} id="incidents_manage" isChecked={field.value} />
              </FormControl>
            )}
          </Field>

          <Field name="tickets_manage">
            {({ field }) => (
              <FormControl isInvalid={errors.tickets_manage && touched.tickets_manage}>
                <FormLabel htmlFor="tickets_manage">управление заявками:</FormLabel>
                <Checkbox {...field} id="tickets_manage" isChecked={field.value} />
              </FormControl>
            )}
          </Field>

          <Field name="monitoring_settings">
            {({ field }) => (
              <FormControl isInvalid={errors.monitoring_settings && touched.monitoring_settings}>
                <FormLabel htmlFor="monitoring_settings">настройки мониторинга:</FormLabel>
                <Checkbox {...field} id="monitoring_settings" isChecked={field.value} />
              </FormControl>
            )}
          </Field>

          <Field name="remote_control_panel">
            {({ field }) => (
              <FormControl isInvalid={errors.remote_control_panel && touched.remote_control_panel}>
                <FormLabel htmlFor="remote_control_panel">доступ к удалённому управлению:</FormLabel>
                <Checkbox {...field} id="remote_control_panel" isChecked={field.value} />
              </FormControl>
            )}
          </Field>

          <Button mt={4} colorScheme="blue" type="submit">
            Сохранить
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default RoleEditForm;
